import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchSayings, createSaying, fetchCategories, fetchLanguages, updateSaying, deleteSaying, fetchTranslations } from '../api';
import { Button } from '../components/ui/button';
import { Pencil, Trash2, CheckCircle2, ExternalLink } from 'lucide-react';

function SayingsPage() {
  const navigate = useNavigate();
  const [sayings, setSayings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [form, setForm] = useState({ saying: '', category_id: '', language_id: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ saying: '', category_id: '', language_id: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [filters, setFilters] = useState({ 
    category_id: '', 
    language_id: '',
    searchText: '',
    approved: ''
  });
  const [searchInput, setSearchInput] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    size: 15,
    total: 0,
    pages: 0
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [sayingsData, categoriesData, languagesData] = await Promise.all([
        fetchSayings({ 
          ...filters,
          approved: filters.approved ? (filters.approved === 'true' ? true : false) : undefined,
          translation_language_id: selectedLanguage || undefined
        }, pagination.page, pagination.size),
        fetchCategories(),
        fetchLanguages()
      ]);
      setSayings(sayingsData.items);
      setPagination({
        page: sayingsData.page,
        size: sayingsData.size,
        total: sayingsData.total,
        pages: sayingsData.pages
      });
      setCategories(categoriesData);
      setLanguages(languagesData);
      setError(null);

      // Load translations if a language is selected
      if (selectedLanguage) {
        try {
          const translationsData = await fetchTranslations({ 
            language_id: selectedLanguage
          });
          setTranslations(translationsData);
        } catch (err) {
          console.error('Failed to load translations:', err);
          setError('Failed to load translations.');
        }
      } else {
        setTranslations([]);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Failed to load data. Please check if the backend server is running at http://localhost:8000');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filters, pagination.page, pagination.size, selectedLanguage]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.saying || !form.category_id || !form.language_id) return;
    try {
      await createSaying({
        saying: form.saying,
        category_id: Number(form.category_id),
        language_id: Number(form.language_id)
      });
      setForm({ saying: '', category_id: '', language_id: '' });
      // Reload all data to refresh sayings list
      await loadData();
    } catch (error) {
      console.error('Error creating saying:', error);
      setError(`Failed to create saying: ${error.message}`);
    }
  };

  const startEdit = s => {
    setEditingId(s.saying_id);
    setEditForm({
      saying: s.saying,
      category_id: s.category_id,
      language_id: s.language_id
    });
  };

  const handleEditChange = e => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = async e => {
    e.preventDefault();
    try {
      await updateSaying(editingId, {
        saying: editForm.saying,
        category_id: Number(editForm.category_id),
        language_id: Number(editForm.language_id)
      });
      setEditingId(null);
      // Reload all data to refresh sayings list
      await loadData();
    } catch (error) {
      console.error('Error updating saying:', error);
      setError(`Failed to update saying: ${error.message}`);
    }
  };

  const handleDelete = async id => {
    try {
      await deleteSaying(id);
      // Reload all data to refresh sayings list
      await loadData();
    } catch (error) {
      console.error('Error deleting saying:', error);
      setError(`Failed to delete saying: ${error.message}`);
    }
  };

  const handleApprove = async (id) => {
    try {
      console.log('Attempting to approve saying with ID:', id);
      await updateSaying(id, { approved: true });
      console.log('Successfully approved saying');
      
      // Reload the sayings to reflect the changes
      await loadData();
    } catch (error) {
      console.error('Failed to approve saying:', error);
      setError(`Failed to approve saying: ${error.message}`);
    }
  };

  const handleFilterChange = e => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleSearchInputChange = e => {
    setSearchInput(e.target.value);
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, searchText: searchInput }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const clearFilters = () => {
    setFilters({ category_id: '', language_id: '', searchText: '', approved: '' });
    setSearchInput('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleLanguageChange = e => {
    setSelectedLanguage(e.target.value);
  };

  // Debug: Log current filters state
  console.log('Current filters:', filters);
  console.log('Current approved filter value:', filters.approved);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-destructive/10 text-destructive rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 space-y-6">
      <h1 className="text-3xl font-bold">Sayings</h1>
      
      {/* Filter Controls */}
      <div className="bg-card p-6 rounded-lg shadow-sm space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Filters</h3>
          <Button variant="outline" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
        <div className="flex flex-wrap gap-4">
          <div className="flex flex-1 min-w-[200px] gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyPress={handleKeyPress}
              placeholder="Search sayings..."
              className="flex-1 px-3 py-2 rounded-md border border-input bg-background"
            />
            <Button onClick={handleSearch}>
              Search
            </Button>
          </div>
          <select 
            name="category_id" 
            value={filters.category_id} 
            onChange={handleFilterChange}
            className="px-3 py-2 rounded-md border border-input bg-background min-w-[150px]"
          >
            <option value="">All Categories</option>
            {categories.map(c => (
              <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
            ))}
          </select>
          <select 
            name="language_id" 
            value={filters.language_id} 
            onChange={handleFilterChange}
            className="px-3 py-2 rounded-md border border-input bg-background min-w-[150px]"
          >
            <option value="">All Languages</option>
            {languages.map(l => (
              <option key={l.language_id} value={l.language_id}>{l.name}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-4 mt-2">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground">Approval Status:</label>
            <select 
              name="approved" 
              value={filters.approved} 
              onChange={handleFilterChange}
              className="px-3 py-2 rounded-md border border-input bg-background min-w-[180px]"
              style={{ backgroundColor: '#f8f9fa', border: '2px solid #007bff' }}
            >
              <option value="">All</option>
              <option value="true">✅ Approved Only</option>
              <option value="false">❌ Not Approved Only</option>
            </select>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium">Show translations in:</label>
          <select
            value={selectedLanguage}
            onChange={handleLanguageChange}
            className="px-3 py-2 rounded-md border border-input bg-background flex-1"
          >
            <option value="">Select a language</option>
            {languages.filter(l => !l.is_default).map(l => (
              <option key={l.language_id} value={l.language_id}>{l.name}</option>
            ))}
          </select>
        </div>
        <div className="text-sm text-muted-foreground">
          Showing {sayings.length} of {pagination.total} sayings
        </div>
      </div>


      {/* Display Sayings */}
      <div className="space-y-4">
        {sayings.length === 0 ? (
          <p className="text-center text-muted-foreground">
            {selectedLanguage 
              ? "No sayings found with translations in the selected language."
              : "No sayings found."}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sayings.map((saying) => {
              const translation = translations.find(t => t.original_saying_id === saying.saying_id);
              return (
                <div
                  key={saying.saying_id}
                  className="bg-card text-card-foreground rounded-lg border p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  {editingId === saying.saying_id ? (
                    <form onSubmit={handleEditSubmit} className="space-y-4">
                      <div className="space-y-4">
                        <input
                          name="saying"
                          value={editForm.saying}
                          onChange={handleEditChange}
                          className="w-full px-3 py-2 rounded-md border border-input bg-background"
                        />
                        <select 
                          name="category_id" 
                          value={editForm.category_id} 
                          onChange={handleEditChange}
                          className="w-full px-3 py-2 rounded-md border border-input bg-background"
                        >
                          {categories.map(c => (
                            <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
                          ))}
                        </select>
                        <select 
                          name="language_id" 
                          value={editForm.language_id} 
                          onChange={handleEditChange}
                          className="w-full px-3 py-2 rounded-md border border-input bg-background"
                        >
                          {languages.map(l => (
                            <option key={l.language_id} value={l.language_id}>{l.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" size="sm">Save</Button>
                        <Button variant="outline" size="sm" onClick={() => setEditingId(null)}>Cancel</Button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{saying.saying}</h3>
                          {translation && (
                            <div>
                              <p className="text-primary italic mt-1">{translation.translated_text}</p>
                              {translation.explanation && (
                                <p className="text-sm text-muted-foreground italic mt-1">
                                  <strong>Explanation:</strong> {translation.explanation}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/sayings/${saying.saying_id}`)}
                            title="Manage Translations"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(saying)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(saying.saying_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          {!saying.approved && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleApprove(saying.saying_id)}
                              title="Approve"
                            >
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <p>Category: {categories.find(c => c.category_id === saying.category_id)?.category_name}</p>
                        <p>Language: {languages.find(l => l.language_id === saying.language_id)?.name}</p>
                        <p>Approved: {saying.approved ? <span className="text-green-600 font-semibold">Yes</span> : <span className="text-destructive font-semibold">No</span>}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-center gap-2 mt-6">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(pagination.page - 1)}
          disabled={pagination.page === 1}
          className="min-w-[80px]"
        >
          Previous
        </Button>
        <div className="flex items-center gap-1">
          {Array.from({ length: pagination.pages }, (_, i) => i + 1)
            .filter(page => {
              if (page === 1 || page === pagination.pages) return true;
              if (Math.abs(page - pagination.page) <= 1) return true;
              return false;
            })
            .map((page, index, array) => {
              if (index > 0 && page - array[index - 1] > 1) {
                return (
                  <React.Fragment key={`ellipsis-${page}`}>
                    <span className="px-2 text-muted-foreground">•••</span>
                    <Button
                      key={page}
                      variant={page === pagination.page ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(page)}
                      className="min-w-[32px] h-8"
                    >
                      {page}
                    </Button>
                  </React.Fragment>
                );
              }
              return (
                <Button
                  key={page}
                  variant={page === pagination.page ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(page)}
                  className="min-w-[32px] h-8"
                >
                  {page}
                </Button>
              );
            })}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handlePageChange(pagination.page + 1)}
          disabled={pagination.page === pagination.pages}
          className="min-w-[80px]"
        >
          Next
        </Button>
      </div>
      <div className="space-y-4">Add new saying</div>
      <form onSubmit={handleSubmit} className="space-y-4 bg-card p-6 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <input
            name="saying"
            value={form.saying}
            onChange={handleChange}
            placeholder="Saying"
            required
            className="px-3 py-2 rounded-md border border-input bg-background"
          />
          <select 
            name="category_id" 
            value={form.category_id} 
            onChange={handleChange} 
            required
            className="px-3 py-2 rounded-md border border-input bg-background"
          >
            <option value="">Select Category</option>
            {categories.map(c => (
              <option key={c.category_id} value={c.category_id}>{c.category_name}</option>
            ))}
          </select>
          <select 
            name="language_id" 
            value={form.language_id} 
            onChange={handleChange} 
            required
            className="px-3 py-2 rounded-md border border-input bg-background"
          >
            <option value="">Select Language</option>
            {languages.map(l => (
              <option key={l.language_id} value={l.language_id}>{l.name}</option>
            ))}
          </select>
        </div>
        <Button type="submit">Add Saying</Button>
      </form>
    </div>
    
  );
}

export default SayingsPage; 